import React, { useState } from 'react';
import {
  Globe, Plus, Edit2, Trash2, X, Eye, Share2,
  Image, Briefcase, Star, Newspaper, Lock, ExternalLink,
  Twitter, Linkedin, Facebook, Check, Upload
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { uploadWebsiteImage, openPortfolioOrSite } from '../../services/websiteAssets';
import {
  buildSiteSharePayload,
  buildPortfolioSharePayload,
  openSocialShareWindows,
} from '../../services/socialShare';
import './AdminPages.css';

const SOCIAL_PLATFORMS = [
  { id:'twitter',   label:'X / Twitter',  icon:Twitter,  color:'#1DA1F2' },
  { id:'linkedin',  label:'LinkedIn',      icon:Linkedin, color:'#0A66C2' },
  { id:'facebook',  label:'Facebook',      icon:Facebook, color:'#1877F2' },
];

const emptyPortfolio = { name:'', desc:'', tech:'', url:'', github:'', logo:'🚀', logoUrl:'' };
const emptyNews = { title:'', excerpt:'', date:'' };
const emptyService = { title:'', description:'', icon:'💡', iconUrl:'' };
const emptyPartner = { name:'', logoText:'', url:'' };

export default function WebsiteManagement() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder } = useAuth();
  const canManage = can('canManageWebsite');

  const [tab, setTab]   = useState('portfolio');
  const [modal, setModal] = useState(null); // { type, item, isEdit }
  const [form, setForm] = useState({});
  /** @type {null | { kind:'site' } | { kind:'portfolio', projectName:string, projectUrl?:string }} */
  const [shareModal, setShareModal] = useState(null);
  /** @type {string[]} platform ids: twitter | linkedin | facebook */
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  if (!canManage) {
    return (
      <div className="admin-page">
        <div className="page-header"><div><h1 className="page-title">Website Management</h1></div></div>
        <div className="perm-denied">
          <div className="perm-denied-icon"><Lock size={26} style={{ color:'var(--text-dim)' }}/></div>
          <p style={{ fontWeight:700 }}>Access Restricted</p>
          <p style={{ color:'var(--text-muted)',fontSize:'0.875rem' }}>Website management is available to Administrators and Founders only.</p>
        </div>
      </div>
    );
  }

  const services = data.websiteServices || [];
  const news = data.websiteNews || [];
  const partners = data.websitePartners || [];

  /* ── Portfolio ── */
  const savePortfolio = async () => {
    const item = { ...form, tech: form.tech?.split(',').map(t=>t.trim()).filter(Boolean)||[] };
    try {
      if (modal.isEdit) {
        await commitData('portfolio', data.portfolio.map(p=>p.id===modal.item.id?{...p,...item}:p));
        showToast('Portfolio item updated');
      } else {
        const newId = modal.draftId || String(Date.now());
        await commitData('portfolio', [...data.portfolio,{ id:newId,...item }]);
        showToast('Portfolio item added to website');
      }
      setModal(null);
    } catch {}
  };

  const deletePortfolio = async (id) => {
    if(!window.confirm('Remove this portfolio item?'))return;
    try {
      await commitData('portfolio', data.portfolio.filter(p=>p.id!==id));
      showToast('Portfolio item removed','warning');
    } catch {}
  };

  const saveService = async () => {
    if(!form.title){showToast('Service title required','error');return;}
    try {
      if(modal.isEdit){
        await commitData('websiteServices', services.map(s=>s.id===modal.item.id?{...s,...form}:s));
        showToast('Service updated');
      } else {
        const newId = modal.serviceDraftId || String(Date.now());
        await commitData('websiteServices', [...services,{ id:newId,...form }]);
        showToast('Service added to website');
      }
      setModal(null);
    } catch {}
  };

  const deleteService = async (id) => {
    try {
      await commitData('websiteServices', services.filter(s=>s.id!==id));
      showToast('Service removed','warning');
    } catch {}
  };

  const saveNews = async () => {
    if(!form.title){showToast('Title required','error');return;}
    try {
      if(modal.isEdit){
        await commitData('websiteNews', news.map(n=>n.id===modal.item.id?{...n,...form}:n));
        showToast('News article updated');
      } else {
        await commitData('websiteNews', [...news,{ id:String(Date.now()),...form,published:false }]);
        showToast('News article saved as draft');
      }
      setModal(null);
    } catch {}
  };

  const togglePublish = async (n) => {
    try {
      await commitData('websiteNews', news.map(item=>item.id===n.id?{...item,published:!item.published}:item));
      showToast(n.published?'Article unpublished':'Article published to website');
    } catch {}
  };

  const deleteNews = async (id) => {
    try {
      await commitData('websiteNews', news.filter(n=>n.id!==id));
      showToast('Article deleted','warning');
    } catch {}
  };

  const savePartner = async () => {
    if(!form.name){showToast('Partner name required','error');return;}
    try {
      if(modal.isEdit){
        await commitData('websitePartners', partners.map(p=>p.id===modal.item.id?{...p,...form}:p));
        showToast('Partner updated');
      } else {
        await commitData('websitePartners', [...partners,{ id:String(Date.now()),...form }]);
        showToast('Partner added to website carousel');
      }
      setModal(null);
    } catch {}
  };

  const deletePartner = async (id) => {
    try {
      await commitData('websitePartners', partners.filter(p=>p.id!==id));
      showToast('Partner removed','warning');
    } catch {}
  };

  /* ── Social Share (real intent URLs: X, Facebook, LinkedIn) ── */
  const doShare = () => {
    if (!selectedPlatforms.length) {
      showToast('Select at least one platform', 'warning');
      return;
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const payload =
      shareModal?.kind === 'portfolio'
        ? buildPortfolioSharePayload(origin, {
            projectName: shareModal.projectName,
            projectUrl: shareModal.projectUrl,
          })
        : buildSiteSharePayload(origin);

    const { opened, attempted } = openSocialShareWindows(selectedPlatforms, payload);
    if (attempted === 0) {
      showToast('Could not build share links', 'error');
      return;
    }
    if (opened === 0) {
      showToast('Pop-up blocked — allow pop-ups for this site and try again', 'error');
    } else if (opened < attempted) {
      showToast(`Opened ${opened} of ${attempted} share windows. Allow pop-ups if any are missing.`, 'warning');
    } else {
      showToast(opened === 1 ? 'Share window opened' : `${opened} share windows opened`);
    }
    setShareModal(null);
    setSelectedPlatforms([]);
  };

  const openModal = (type, item=null) => {
    const isEdit = !!item;
    setModal({
      type,
      item,
      isEdit,
      draftId: type === 'portfolio' && !isEdit ? String(Date.now()) : undefined,
      serviceDraftId: type === 'service' && !isEdit ? String(Date.now()) : undefined,
    });
    if(type==='portfolio') setForm(item?{...item,tech:item.tech?.join(', ')||''}:{...emptyPortfolio});
    else if(type==='service') setForm(item?{...item}:{...emptyService});
    else if(type==='news')    setForm(item||emptyNews);
    else if(type==='partner') setForm(item||emptyPartner);
  };

  const portfolioLogoPickId = `portfolio-logo-file-${modal?.draftId || modal?.item?.id || 'x'}`;
  const serviceIconPickId = `service-icon-file-${modal?.serviceDraftId || modal?.item?.id || 'x'}`;

  const handlePortfolioModalLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !modal) return;
    const id = modal.isEdit ? modal.item.id : modal.draftId;
    if (!id) return;
    try {
      const url = await uploadWebsiteImage(`portfolio/${id}/logo`, file);
      setForm((f) => ({ ...f, logoUrl: url }));
      showToast('Logo image uploaded');
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    }
  };

  const handleServiceModalIcon = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !modal) return;
    const id = modal.isEdit ? modal.item.id : modal.serviceDraftId;
    if (!id) return;
    try {
      const url = await uploadWebsiteImage(`services/${id}/icon`, file);
      setForm((f) => ({ ...f, iconUrl: url }));
      showToast('Icon image uploaded');
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    }
  };

  const handlePortfolioCardLogoUpload = async (item, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const url = await uploadWebsiteImage(`portfolio/${item.id}/logo`, file);
      await commitData('portfolio', data.portfolio.map((p) => (p.id === item.id ? { ...p, logoUrl: url } : p)));
      showToast('Logo updated');
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    }
  };

  const handleServiceCardIconUpload = async (item, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const url = await uploadWebsiteImage(`services/${item.id}/icon`, file);
      await commitData('websiteServices', services.map((s) => (s.id === item.id ? { ...s, iconUrl: url } : s)));
      showToast('Icon updated');
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    }
  };

  const tabDefs = [
    { id:'portfolio', label:'Portfolio',  icon:Image,     count:data.portfolio.length },
    { id:'services',  label:'Services',   icon:Briefcase, count:services.length },
    { id:'news',      label:'News',       icon:Newspaper, count:news.length },
    { id:'partners',  label:'Partners',   icon:Star,      count:partners.length },
  ];

  const saveMap = { portfolio:savePortfolio, service:saveService, news:saveNews, partner:savePartner };
  const modalTitleMap = { portfolio:'Portfolio Item', service:'Service', news:'News Article', partner:'Partner' };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Website Management</h1>
          <p className="page-subtitle">Control what appears on the public-facing website</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-ghost" onClick={()=>window.open(`${window.location.origin}/`,'_blank','noopener,noreferrer')} style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.82rem' }}>
            <Eye size={14}/> Preview Site
          </button>
          <button type="button" className="btn-outline" onClick={()=>setShareModal({ kind:'site' })} style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.82rem' }}>
            <Share2 size={14}/> Share to Social
          </button>
          <button className="btn-gold" onClick={()=>openModal(tab==='portfolio'?'portfolio':tab==='services'?'service':tab==='news'?'news':'partner')}>
            <Plus size={15}/> Add {tab==='portfolio'?'Project':tab==='services'?'Service':tab==='news'?'Article':'Partner'}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="filter-tabs" style={{ marginBottom:22,width:'fit-content' }}>
        {tabDefs.map(t=>(
          <button key={t.id} className={`filter-tab ${tab===t.id?'active':''}`} onClick={()=>setTab(t.id)}>
            {t.label} <span style={{ marginLeft:4,opacity:0.7 }}>({t.count})</span>
          </button>
        ))}
      </div>

      {/* ── PORTFOLIO TAB ── */}
      {tab==='portfolio' && (
        <div className="cards-grid">
          {data.portfolio.map(p=>(
            <div key={p.id} className="card" style={{ padding:20 }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                  <label
                    title="Upload logo image"
                    style={{ cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',width:44,height:44,borderRadius:'var(--radius-md)',border:'1px dashed var(--border-accent)',flexShrink:0,overflow:'hidden',background:'var(--surface-2)' }}
                  >
                    <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml" style={{ display:'none' }} onChange={(e)=>handlePortfolioCardLogoUpload(p,e)} />
                    {p.logoUrl ? (
                      <img src={p.logoUrl} alt="" style={{ width:'100%',height:'100%',objectFit:'contain' }} />
                    ) : (
                      <span style={{ fontSize:'1.6rem' }}>{p.logo || '📁'}</span>
                    )}
                  </label>
                  <p style={{ fontWeight:700,fontSize:'0.95rem' }}>{p.name}</p>
                </div>
                <div style={{ display:'flex',gap:4 }}>
                  <button type="button" className="btn-ghost" style={{ padding:'5px' }} title="Open live site or portfolio page" onClick={()=>openPortfolioOrSite(p)}><Eye size={13}/></button>
                  <button type="button" className="btn-ghost" style={{ padding:'5px' }} title="Share this project" onClick={()=>setShareModal({ kind:'portfolio', projectName:p.name, projectUrl:p.url })}><Share2 size={13}/></button>
                  <button className="btn-ghost" style={{ padding:'5px' }} onClick={()=>openModal('portfolio',p)}><Edit2 size={13}/></button>
                  {isFounder&&<button className="btn-ghost" style={{ padding:'5px',color:'var(--danger)' }} onClick={()=>deletePortfolio(p.id)}><Trash2 size={13}/></button>}
                </div>
              </div>
              <p style={{ fontSize:'0.8rem',color:'var(--text-muted)',lineHeight:1.6,marginBottom:10 }}>{p.desc}</p>
              <div className="tech-tags">{p.tech?.map(t=><span key={t} className="tech-tag">{t}</span>)}</div>
              <div style={{ display:'flex',gap:8,marginTop:12 }}>
                {p.url&&p.url!=='#'&&<a href={p.url} target="_blank" rel="noreferrer" style={{ fontSize:'0.75rem',color:'var(--secondary-color)',display:'flex',alignItems:'center',gap:4 }}><ExternalLink size={11}/>Live</a>}
                {p.github&&<a href={p.github} target="_blank" rel="noreferrer" style={{ fontSize:'0.75rem',color:'var(--text-muted)',display:'flex',alignItems:'center',gap:4 }}><Globe size={11}/>GitHub</a>}
              </div>
            </div>
          ))}
          <div className="add-card" onClick={()=>openModal('portfolio')}>
            <Plus size={26}/><span>Add Portfolio Item</span>
          </div>
        </div>
      )}

      {/* ── SERVICES TAB ── */}
      {tab==='services' && (
        <div className="cards-grid">
          {services.map(s=>(
            <div key={s.id} className="card" style={{ padding:20 }}>
              <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10 }}>
                <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                  <label
                    title="Upload icon image"
                    style={{ cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',width:44,height:44,borderRadius:'var(--radius-md)',border:'1px dashed var(--border-accent)',flexShrink:0,overflow:'hidden',background:'var(--surface-2)' }}
                  >
                    <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml" style={{ display:'none' }} onChange={(e)=>handleServiceCardIconUpload(s,e)} />
                    {s.iconUrl ? (
                      <img src={s.iconUrl} alt="" style={{ width:'100%',height:'100%',objectFit:'contain' }} />
                    ) : (
                      <span style={{ fontSize:'1.5rem' }}>{s.icon || '💡'}</span>
                    )}
                  </label>
                  <p style={{ fontWeight:700 }}>{s.title}</p>
                </div>
                <div style={{ display:'flex',gap:4 }}>
                  <button className="btn-ghost" style={{ padding:'5px' }} onClick={()=>openModal('service',s)}><Edit2 size={13}/></button>
                  {isFounder&&<button className="btn-ghost" style={{ padding:'5px',color:'var(--danger)' }} onClick={()=>deleteService(s.id)}><Trash2 size={13}/></button>}
                </div>
              </div>
              <p style={{ fontSize:'0.8rem',color:'var(--text-muted)',lineHeight:1.6 }}>{s.description}</p>
            </div>
          ))}
          <div className="add-card" onClick={()=>openModal('service')}>
            <Plus size={26}/><span>Add Service</span>
          </div>
        </div>
      )}

      {/* ── NEWS TAB ── */}
      {tab==='news' && (
        <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
          {news.map(n=>(
            <div key={n.id} className="card" style={{ padding:18,display:'flex',alignItems:'flex-start',gap:14 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:5 }}>
                  <p style={{ fontWeight:700,fontSize:'0.95rem' }}>{n.title}</p>
                  <span className={`badge ${n.published?'badge-success':'badge-gray'}`}>{n.published?'Published':'Draft'}</span>
                </div>
                <p style={{ fontSize:'0.8rem',color:'var(--text-muted)',lineHeight:1.6,marginBottom:6 }}>{n.excerpt}</p>
                <p style={{ fontSize:'0.73rem',color:'var(--text-dim)' }}>{n.date&&new Date(n.date).toLocaleDateString('en-ZA',{day:'numeric',month:'long',year:'numeric'})}</p>
              </div>
              <div style={{ display:'flex',gap:6,flexShrink:0 }}>
                <button className={`btn-${n.published?'ghost':'outline'}`} style={{ padding:'6px 12px',fontSize:'0.75rem' }} onClick={()=>togglePublish(n)}>
                  {n.published?'Unpublish':'Publish'}
                </button>
                <button className="btn-ghost" style={{ padding:'6px 10px',fontSize:'0.75rem' }} onClick={()=>openModal('news',n)}><Edit2 size={12}/></button>
                {isFounder&&<button className="btn-ghost" style={{ padding:'6px 10px',fontSize:'0.75rem',color:'var(--danger)' }} onClick={()=>deleteNews(n.id)}><Trash2 size={12}/></button>}
              </div>
            </div>
          ))}
          <div className="add-card" style={{ minHeight:80 }} onClick={()=>openModal('news')}>
            <Plus size={22}/><span>New Article</span>
          </div>
        </div>
      )}

      {/* ── PARTNERS TAB ── */}
      {tab==='partners' && (
        <div>
          <p className="card-subtitle" style={{ marginBottom:14 }}>These logos appear in the auto-scrolling carousel on the homepage.</p>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12 }}>
            {partners.map(p=>(
              <div key={p.id} className="card" style={{ padding:16,textAlign:'center',position:'relative' }}>
                <div style={{ width:48,height:48,borderRadius:'var(--radius-md)',background:'rgba(201,162,39,0.08)',border:'1px solid var(--border-accent)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 10px',fontFamily:'var(--font-mono)',fontWeight:700,fontSize:'0.8rem',color:'var(--secondary-color)',textTransform:'uppercase' }}>{p.logoText}</div>
                <p style={{ fontWeight:700,fontSize:'0.875rem',marginBottom:8 }}>{p.name}</p>
                <div style={{ display:'flex',gap:5,justifyContent:'center' }}>
                  {p.url&&<a href={p.url} target="_blank" rel="noreferrer" className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem' }}><ExternalLink size={10}/></a>}
                  <button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem' }} onClick={()=>openModal('partner',p)}><Edit2 size={10}/></button>
                  {isFounder&&<button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem',color:'var(--danger)' }} onClick={()=>deletePartner(p.id)}><Trash2 size={10}/></button>}
                </div>
              </div>
            ))}
            <div className="add-card" style={{ minHeight:120 }} onClick={()=>openModal('partner')}>
              <Plus size={22}/><span>Add Partner</span>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT / ADD MODAL ── */}
      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{modal.isEdit?'Edit':'Add'} {modalTitleMap[modal.type]}</p>
              <button className="modal-close" onClick={()=>setModal(null)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              {/* Portfolio form */}
              {modal.type==='portfolio'&&<>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Project Name</label><input className="form-input" value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Project name"/></div>
                  <div className="form-group">
                    <label className="form-label">Logo</label>
                    <div style={{ display:'flex',alignItems:'center',gap:12,flexWrap:'wrap' }}>
                      <label className="btn-outline" style={{ display:'inline-flex',alignItems:'center',gap:8,cursor:'pointer',margin:0 }}>
                        <Upload size={14}/>
                        Upload image
                        <input id={portfolioLogoPickId} type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml" style={{ display:'none' }} onChange={handlePortfolioModalLogo} />
                      </label>
                      {form.logoUrl && <img src={form.logoUrl} alt="" style={{ width:48,height:48,objectFit:'contain',borderRadius:'var(--radius-sm)',border:'1px solid var(--border-color)' }} />}
                    </div>
                    <p style={{ fontSize:'0.72rem',color:'var(--text-dim)',marginTop:6 }}>Or use an emoji if you prefer no image.</p>
                    <input className="form-input" style={{ marginTop:8 }} value={form.logo||''} onChange={e=>setForm({...form,logo:e.target.value})} placeholder="🚀 fallback emoji"/>
                  </div>
                </div>
                <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={3} value={form.desc||''} onChange={e=>setForm({...form,desc:e.target.value})} placeholder="Short description..."/></div>
                <div className="form-group"><label className="form-label">Technologies (comma-separated)</label><input className="form-input" value={form.tech||''} onChange={e=>setForm({...form,tech:e.target.value})} placeholder="React, Node.js, MySQL"/></div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Live URL</label><input className="form-input" value={form.url||''} onChange={e=>setForm({...form,url:e.target.value})} placeholder="https://..."/></div>
                  <div className="form-group"><label className="form-label">GitHub URL</label><input className="form-input" value={form.github||''} onChange={e=>setForm({...form,github:e.target.value})} placeholder="https://github.com/..."/></div>
                </div>
              </>}
              {/* Service form */}
              {modal.type==='service'&&<>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Title</label><input className="form-input" value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Service title"/></div>
                  <div className="form-group">
                    <label className="form-label">Icon</label>
                    <div style={{ display:'flex',alignItems:'center',gap:12,flexWrap:'wrap' }}>
                      <label className="btn-outline" style={{ display:'inline-flex',alignItems:'center',gap:8,cursor:'pointer',margin:0 }}>
                        <Upload size={14}/>
                        Upload image
                        <input id={serviceIconPickId} type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml" style={{ display:'none' }} onChange={handleServiceModalIcon} />
                      </label>
                      {form.iconUrl && <img src={form.iconUrl} alt="" style={{ width:48,height:48,objectFit:'contain',borderRadius:'var(--radius-sm)',border:'1px solid var(--border-color)' }} />}
                    </div>
                    <input className="form-input" style={{ marginTop:8 }} value={form.icon||''} onChange={e=>setForm({...form,icon:e.target.value})} placeholder="💡 fallback emoji"/>
                  </div>
                </div>
                <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={4} value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Service description..."/></div>
              </>}
              {/* News form */}
              {modal.type==='news'&&<>
                <div className="form-group"><label className="form-label">Title</label><input className="form-input" value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Article title"/></div>
                <div className="form-group"><label className="form-label">Date</label><input className="form-input" type="date" value={form.date||''} onChange={e=>setForm({...form,date:e.target.value})}/></div>
                <div className="form-group"><label className="form-label">Excerpt / Body</label><textarea className="form-input" rows={5} value={form.excerpt||''} onChange={e=>setForm({...form,excerpt:e.target.value})} placeholder="Article content..."/></div>
              </>}
              {/* Partner form */}
              {modal.type==='partner'&&<>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Partner Name</label><input className="form-input" value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})} placeholder="AWS"/></div>
                  <div className="form-group"><label className="form-label">Logo Text / Abbr</label><input className="form-input" value={form.logoText||''} onChange={e=>setForm({...form,logoText:e.target.value})} placeholder="aws"/></div>
                </div>
                <div className="form-group"><label className="form-label">Website URL</label><input className="form-input" value={form.url||''} onChange={e=>setForm({...form,url:e.target.value})} placeholder="https://..."/></div>
              </>}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn-gold" onClick={saveMap[modal.type]}>{modal.isEdit?'Save Changes':'Add to Website'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── SOCIAL SHARE MODAL ── */}
      {shareModal && (
        <div className="modal-overlay" onClick={()=>{ setShareModal(null); setSelectedPlatforms([]); }}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">Share to Social Media</p>
              <button type="button" className="modal-close" onClick={()=>{ setShareModal(null); setSelectedPlatforms([]); }}><X size={18}/></button>
            </div>
            {shareModal?.kind === 'portfolio' && (
              <p style={{ fontSize:'0.82rem',color:'var(--secondary-color)',marginBottom:12,fontWeight:600 }}>
                Project: {shareModal.projectName}
              </p>
            )}
            <p style={{ fontSize:'0.85rem',color:'var(--text-muted)',marginBottom:18 }}>
              Choose networks, then Share now — we open each platform’s official share page in a new tab (you finish the post there).
            </p>
            <div style={{ display:'flex',flexDirection:'column',gap:10,marginBottom:22 }}>
              {SOCIAL_PLATFORMS.map(p=>{
                const selected = selectedPlatforms.includes(p.id);
                return (
                  <div key={p.id} role="button" tabIndex={0}
                    onKeyDown={(e)=>{ if(e.key==='Enter'||e.key===' ') { e.preventDefault(); setSelectedPlatforms(prev=>selected?prev.filter(x=>x!==p.id):[...prev,p.id]); }}}
                    onClick={()=>setSelectedPlatforms(prev=>selected?prev.filter(x=>x!==p.id):[...prev,p.id])}
                    style={{ display:'flex',alignItems:'center',gap:14,padding:'13px 16px',background:selected?`${p.color}12`:'var(--surface-2)',border:`1px solid ${selected?p.color:'var(--border-color)'}`,borderRadius:'var(--radius-md)',cursor:'pointer',transition:'var(--transition)' }}>
                    <p.icon size={20} style={{ color:p.color }}/>
                    <span style={{ fontWeight:600,flex:1 }}>{p.label}</span>
                    <div style={{ width:20,height:20,borderRadius:4,border:`2px solid ${selected?p.color:'var(--border-color)'}`,background:selected?p.color:'transparent',display:'flex',alignItems:'center',justifyContent:'center' }}>
                      {selected&&<Check size={13} color="#fff"/>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={()=>{ setShareModal(null); setSelectedPlatforms([]); }}>Cancel</button>
              <button type="button" className="btn-gold" onClick={doShare}><Share2 size={14}/> Share now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
